interface GraphNode {
	id: string;
	type?: string;
}

interface GraphEdge {
	source: string;
	target: string;
	sourceHandle?: string | null;
}

export type OpenSourceHandle = "normalEdge" | "yesEdge" | "noEdge";

export interface OpenSource {
	nodeId: string;
	handle: OpenSourceHandle;
}

export interface DisconnectedAnalysis {
	/** True when the starter node has no outgoing edges but other nodes form a chain */
	starterDisconnected: boolean;
	/** Node IDs that are not reachable from the starter node */
	orphanedNodeIds: string[];
	/** Orphan node IDs with no incoming edge — valid reconnection targets */
	orphanRootIds: string[];
	/** Unused outgoing source slots on reachable nodes — valid drag-reconnect sources. Empty when no orphans. */
	openSources: OpenSource[];
}

const LINEAR_SOURCE_NODE_TYPES = new Set<string>([
	"starterNode",
	"emailNode",
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
	"linkedinVisitNode",
	"linkedinRemoveInviteNode",
]);

const CONDITIONAL_NODE_TYPE = "conditionalNode";

/**
 * BFS from a start node through an undirected adjacency list.
 * Returns the set of reachable node IDs.
 */
function bfs(startId: string, adjacency: Map<string, Set<string>>): Set<string> {
	const visited = new Set<string>();
	const queue: string[] = [startId];

	visited.add(startId);

	while (queue.length > 0) {
		const current = queue.shift()!;
		const neighbors = adjacency.get(current);

		if (neighbors) {
			for (const neighbor of neighbors) {
				if (!visited.has(neighbor)) {
					visited.add(neighbor);
					queue.push(neighbor);
				}
			}
		}
	}

	return visited;
}

function buildAdjacency(nodes: GraphNode[], edges: GraphEdge[]): Map<string, Set<string>> {
	const adjacency = new Map<string, Set<string>>();
	const nodeIds = new Set(nodes.map((node) => node.id));

	for (const node of nodes) {
		adjacency.set(node.id, new Set());
	}

	// Skip zombie edges (edges referencing nodes that no longer exist) and
	// self-loops. Zombie edges would otherwise cause BFS to "visit" non-existent
	// node IDs; self-loops don't contribute to connectivity and break orphan
	// root detection.
	for (const edge of edges) {
		if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
			continue;
		}

		if (edge.source === edge.target) {
			continue;
		}

		adjacency.get(edge.source)?.add(edge.target);
		adjacency.get(edge.target)?.add(edge.source);
	}

	return adjacency;
}

/**
 * Checks whether all nodes in the campaign graph are reachable from the starter node.
 * Returns true if the graph is connected, false if any nodes are orphaned/disconnected.
 */
export function isGraphConnected(nodes: GraphNode[], edges: GraphEdge[]): boolean {
	if (nodes.length <= 1) {
		return true;
	}

	const starterNode = nodes.find((node) => node.type === "starterNode");

	if (!starterNode) {
		return false;
	}

	const adjacency = buildAdjacency(nodes, edges);
	const visited = bfs(starterNode.id, adjacency);

	return visited.size === nodes.length;
}

/**
 * Returns source handles on the given nodes that have no outgoing edge — these
 * are slots where the user can drag-connect to reattach an orphan chain.
 * Linear nodes expose a single `normalEdge` slot; conditional nodes expose
 * separate `yesEdge` / `noEdge` slots.
 */
function getOpenSources(reachableNodes: GraphNode[], edges: GraphEdge[]): OpenSource[] {
	const openSources: OpenSource[] = [];

	for (const node of reachableNodes) {
		const outgoing = edges.filter((edge) => edge.source === node.id);

		if (node.type === CONDITIONAL_NODE_TYPE) {
			const hasYes = outgoing.some((edge) => edge.sourceHandle === "yesEdge");
			const hasNo = outgoing.some((edge) => edge.sourceHandle === "noEdge");

			if (!hasYes) {
				openSources.push({ nodeId: node.id, handle: "yesEdge" });
			}

			if (!hasNo) {
				openSources.push({ nodeId: node.id, handle: "noEdge" });
			}
		} else if (LINEAR_SOURCE_NODE_TYPES.has(node.type ?? "")) {
			const hasNormal = outgoing.some(
				(edge) => !edge.sourceHandle || edge.sourceHandle === "normalEdge"
			);

			if (!hasNormal) {
				openSources.push({ nodeId: node.id, handle: "normalEdge" });
			}
		}
	}

	return openSources;
}

/**
 * Analyzes disconnected nodes to distinguish between:
 * 1. Starter disconnected — starter has no outgoing edge but other nodes form a connected chain.
 *    In this case the user can manually drag-connect from the starter node.
 * 2. Orphaned nodes — nodes that are truly isolated. The user can either
 *    drag-reconnect them from an open source handle or remove them.
 */
export function analyzeDisconnectedNodes(
	nodes: GraphNode[],
	edges: GraphEdge[]
): DisconnectedAnalysis {
	if (nodes.length <= 1) {
		return {
			starterDisconnected: false,
			orphanedNodeIds: [],
			orphanRootIds: [],
			openSources: [],
		};
	}

	const starterNode = nodes.find((node) => node.type === "starterNode");

	if (!starterNode) {
		return {
			starterDisconnected: false,
			orphanedNodeIds: nodes.map((n) => n.id),
			orphanRootIds: [],
			openSources: [],
		};
	}

	const adjacency = buildAdjacency(nodes, edges);
	const reachable = bfs(starterNode.id, adjacency);

	if (reachable.size === nodes.length) {
		return {
			starterDisconnected: false,
			orphanedNodeIds: [],
			orphanRootIds: [],
			openSources: [],
		};
	}

	const unreachableIds = nodes.filter((n) => !reachable.has(n.id)).map((n) => n.id);
	const nodeIds = new Set(nodes.map((n) => n.id));

	// Check if the starter node has no outgoing edges (the link was broken)
	const starterHasOutgoing = edges.some((edge) => edge.source === starterNode.id);
	const starterDisconnected = !starterHasOutgoing && unreachableIds.length > 0;

	// Orphan roots: orphan nodes with no incoming edge from any other real node —
	// these are the valid reconnection targets. Self-loops are ignored because
	// they don't represent a real predecessor.
	const orphanRootIds = unreachableIds.filter(
		(orphanId) =>
			!edges.some(
				(edge) => edge.target === orphanId && edge.source !== orphanId && nodeIds.has(edge.source)
			)
	);

	const reachableNodes = nodes.filter((n) => reachable.has(n.id));
	const openSources = getOpenSources(reachableNodes, edges);

	return {
		starterDisconnected,
		orphanedNodeIds: unreachableIds,
		orphanRootIds,
		openSources,
	};
}
